import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { handlePostRequest } from '../../utils/api-handler.util'
import { showToaster } from '../../utils/show-toaster.util'
import PhoneInput from 'react-phone-number-input'
import 'react-phone-number-input/style.css'
import { useNavigate } from 'react-router-dom'
import USER_TYPE from '../../constants/userType'
import TextareaField from '../common/TextareaField'
import SelectField from '../common/SelectField'
import InputField from '../common/InputField'
import { useAuth } from '../../contexts/AuthContext'

const fields = [
  { name: 'name', label: 'Enter Your Name' },
  { name: 'shopName', label: 'Enter Shop Name' },
  { name: 'buildingName', label: 'Building' },
  { name: 'floor', label: 'Floor' },
  { name: 'shopNumber', label: 'Shop NO' },
  { name: 'description', label: 'Shop Description', type: 'textarea' },
  { name: 'password', label: 'Password', type: 'password' },
  { name: 'passwordConfirmation', label: 'Confirm Password', type: 'password' },
]

export default function ShopSignUpForm({ data, onNext, userType }) {
  const { apiEndpoint, title } = {
    [USER_TYPE.retailer]: {
      apiEndpoint: '/retailers/register',
      title: 'Register As A Retailer',
    },
    [USER_TYPE.wholeseller]: {
      apiEndpoint: '/wholesellers/register',
      title: 'Register As A Wholeseller',
    },
  }[userType]

  const onSuccess = onNext
  const { otpDetails, phone } = data

  // default the phone from the data into the form state
  const [formState, setFormState] = useState(
    Object.fromEntries(
      fields.map((f) => (f.name === 'phoneNumber' ? [f.name, phone || ''] : [f.name, '']))
    )
  )

  const navigate = useNavigate()
  const { logout } = useAuth()

  const mutation = useMutation({
    mutationFn: (formData) => handlePostRequest(apiEndpoint, formData),
    onSuccess: async (data) => {
      await logout()
      showToaster('success', 'Registration successful!')
      navigate('/login')
      if (onSuccess) onSuccess(data)
    },
  })

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormState((prev) => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!otpDetails?.otp?.isConfirmed) {
      throw new Error('Please verify your phone number first')
    }

    const shopName = formState.shopName?.toString() || ''
    const otpPhone = otpDetails.otp.phoneNumber?.toString() || ''

    mutation.mutateAsync({
      name: formState.name,
      shopName,
      buildingName: formState.buildingName,
      floor: formState.floor,
      shopNumber: formState.shopNumber,
      description: formState.description,
      password: formState.password,
      passwordConfirmation: formState.passwordConfirmation,
      otpVerified: true,
      otpId: otpDetails.otp.id,
      otpCode: otpDetails.otp.otpCode,
      phoneNumber: otpPhone,
    })
  }

  const renderInputField = (field) => {
    console.log('Rendering field:', field)
    if (field.name === 'phoneNumber') {
      return (
        <>
          <label className="block text-gray-700 font-medium mb-1">
            {field.label} <span className="text-red-600">*</span>
          </label>
          <PhoneInput
            international
            defaultCountry="KE"
            value={formState.phoneNumber}
            onChange={(value) => setFormState((prev) => ({ ...prev, phoneNumber: value }))}
            className="w-full bg-secondary-50 border-b border-black rounded px-3 py-2 text-lg"
            required
            disabled
          />
        </>
      )
    }
    if (field.type === 'select') {
      return (
        <SelectField
          name={field.name}
          value={formState[field.name]}
          onChange={handleChange}
          required
        >
          <option value="">Select {field.label}</option>
          {field.options.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </SelectField>
      )
    }

    if (field.type === 'textarea') {
      return (
        <TextareaField
          name={field.name}
          value={formState[field.name]}
          onChange={handleChange}
          placeholder={field.label}
          required
          label={field.label}
        />
      )
    }

    return (
      <InputField
        name={field.name}
        value={formState[field.name]}
        onChange={handleChange}
        placeholder={field.label}
        required
        label={field.label}
        type={field.type || 'text'}
        autoComplete={field.name === 'password' ? 'new-password' : 'off'}
      />
    )
  }

  return (
    <div>
      <div className="flex flex-col items-center">
        <h1 className="font-bold text-2xl mt-2">{title}</h1>
      </div>
      <form onSubmit={handleSubmit} className="flex flex-col gap-4  mt-4">
        {fields.map((field) => (
          <div key={field.name}>{renderInputField(field)}</div>
        ))}
        <button
          type="submit"
          disabled={mutation.isLoading}
          className={`w-full mt-4 py-3 bg-gradient-to-r from-[#DE8516] to-[#F08A0A] text-white font-semibold text-lg rounded-lg shadow ${
            mutation.isLoading ? 'opacity-70 cursor-not-allowed' : ''
          }`}
        >
          {mutation.isLoading ? 'SIGNING UP...' : 'SIGN UP'}
        </button>
      </form>
    </div>
  )
}
